import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Auth API
export const authAPI = {
  // Accepte un objet { email, password } pour cohérence avec AuthContext
  login: (data) => axios.post(`${API_URL}/auth/login`, data),
  getMe: () => axios.get(`${API_URL}/auth/me`),
  verifySession: () => axios.post(`${API_URL}/auth/verify-session`),
  refresh: (refreshToken) => axios.post(`${API_URL}/auth/refresh`, { refresh_token: refreshToken }),
  logout: (refreshToken) => axios.post(`${API_URL}/auth/logout`, { refresh_token: refreshToken }),
};

// Email API
export const emailAPI = {
  getEmails: (folder, page = 1, limit = 50) => 
    axios.get(`${API_URL}/emails/${folder}`, { params: { page, limit } }),
  getEmail: (emailId) => axios.get(`${API_URL}/emails/message/${emailId}`),
  sendEmail: (data) => axios.post(`${API_URL}/emails/send`, data),
  saveDraft: (data) => axios.post(`${API_URL}/emails/drafts`, data),
  markAsRead: (emailId, isRead = true) => 
    axios.put(`${API_URL}/emails/${emailId}/read`, null, { params: { is_read: isRead } }),
  toggleFlag: (emailId, isFlagged = true) => 
    axios.put(`${API_URL}/emails/${emailId}/flag`, null, { params: { is_flagged: isFlagged } }),
  moveEmail: (emailId, folder) => 
    axios.put(`${API_URL}/emails/${emailId}/move`, null, { params: { folder } }),
  deleteEmail: (emailId) => axios.delete(`${API_URL}/emails/${emailId}`),
  searchEmails: (query) => axios.get(`${API_URL}/emails/search/${query}`),
  advancedSearch: (params) => axios.post(`${API_URL}/emails/search/advanced`, null, { params }),
  syncEmails: (folder = 'INBOX') => axios.post(`${API_URL}/email/sync`, null, { params: { folder } }),
  syncAllFolders: () => axios.post(`${API_URL}/email/sync-all`),
};

// Folders API
export const foldersAPI = {
  getFolders: () => axios.get(`${API_URL}/folders`),
  createFolder: (name) => axios.post(`${API_URL}/folders`, null, { params: { name } }),
  deleteFolder: (folderId) => axios.delete(`${API_URL}/folders/${folderId}`),
};

// Signatures API
export const signaturesAPI = {
  getSignatures: () => axios.get(`${API_URL}/signatures`),
  createSignature: (data) => axios.post(`${API_URL}/signatures`, data),
  updateSignature: (signatureId, data) => axios.put(`${API_URL}/signatures/${signatureId}`, data),
  deleteSignature: (signatureId) => axios.delete(`${API_URL}/signatures/${signatureId}`),
};

// Calendar API
export const calendarAPI = {
  getEvents: (start, end) => axios.get(`${API_URL}/calendar/events`, { params: { start, end } }),
  createEvent: (data) => axios.post(`${API_URL}/calendar/events`, data),
  updateEvent: (eventId, data) => axios.put(`${API_URL}/calendar/events/${eventId}`, data),
  deleteEvent: (eventId) => axios.delete(`${API_URL}/calendar/events/${eventId}`),
  getReminders: () => axios.get(`${API_URL}/calendar/reminders`),
  dismissReminder: (eventId) => axios.put(`${API_URL}/calendar/events/${eventId}/dismiss-reminder`),
};

// Contacts API
export const contactsAPI = {
  getContacts: () => axios.get(`${API_URL}/contacts`),
  getContact: (contactId) => axios.get(`${API_URL}/contacts/${contactId}`),
  createContact: (data) => axios.post(`${API_URL}/contacts`, data),
  updateContact: (contactId, data) => axios.put(`${API_URL}/contacts/${contactId}`, data),
  deleteContact: (contactId) => axios.delete(`${API_URL}/contacts/${contactId}`),
  exportContacts: (format = 'csv') => axios.get(`${API_URL}/contacts/export`, { params: { format }, responseType: 'blob' }),
  importContacts: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/contacts/import`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// Contact Groups API
export const contactGroupsAPI = {
  getGroups: () => axios.get(`${API_URL}/contact-groups`),
  createGroup: (data) => axios.post(`${API_URL}/contact-groups`, data),
  deleteGroup: (groupId) => axios.delete(`${API_URL}/contact-groups/${groupId}`),
};

// Tasks API
export const tasksAPI = {
  getTasks: (status) => axios.get(`${API_URL}/tasks`, { params: { status } }),
  createTask: (data) => axios.post(`${API_URL}/tasks`, data),
  updateTask: (taskId, data) => axios.put(`${API_URL}/tasks/${taskId}`, data),
  deleteTask: (taskId) => axios.delete(`${API_URL}/tasks/${taskId}`),
};

// Email Filters API
export const emailFiltersAPI = {
  getFilters: () => axios.get(`${API_URL}/email-filters`),
  createFilter: (data) => axios.post(`${API_URL}/email-filters`, data),
  updateFilter: (filterId, data) => axios.put(`${API_URL}/email-filters/${filterId}`, data),
  deleteFilter: (filterId) => axios.delete(`${API_URL}/email-filters/${filterId}`),
  applyFilters: () => axios.post(`${API_URL}/email-filters/apply`),
};

// Attachments API
export const attachmentsAPI = {
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/attachments/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadUrl: (attachmentId) => {
    // Utilise la bonne clé de stockage promail_token (et non l'ancienne 'token')
    const token = localStorage.getItem('promail_token') || '';
    return `${API_URL}/attachments/${attachmentId}?token=${token}`;
  },
  delete: (attachmentId) => axios.delete(`${API_URL}/attachments/${attachmentId}`),
};

// Settings API
export const settingsAPI = {
  getSettings: () => axios.get(`${API_URL}/settings`),
  updateSettings: (data) => axios.put(`${API_URL}/settings`, data),
};

// User Profile API
export const profileAPI = {
  getProfile: () => axios.get(`${API_URL}/user/profile`),
  updateProfile: (data) => axios.put(`${API_URL}/user/profile`, data),
};

// Admin API
export const adminAPI = {
  login: (email, password) => axios.post(`${API_URL}/admin/login`, { email, password }),
  getConfig: () => axios.get(`${API_URL}/admin/config`),
  updateConfig: (data) => axios.put(`${API_URL}/admin/config`, data),
  getUsers: () => axios.get(`${API_URL}/admin/users`),
  deleteUser: (userId) => axios.delete(`${API_URL}/admin/users/${userId}`),
  toggleAdmin: (userId) => axios.put(`${API_URL}/admin/users/${userId}/toggle-admin`),
  // Domain management
  getDomains: () => axios.get(`${API_URL}/admin/domains`),
  createDomain: (data) => axios.post(`${API_URL}/admin/domains`, data),
  updateDomain: (domainId, data) => axios.put(`${API_URL}/admin/domains/${domainId}`, data),
  deleteDomain: (domainId) => axios.delete(`${API_URL}/admin/domains/${domainId}`),
};

// Public domains for login
export const domainsAPI = {
  getPublicDomains: () => axios.get(`${API_URL}/domains/public`),
};

// Stats API
export const statsAPI = {
  getStats: () => axios.get(`${API_URL}/stats`),
};

// Public Config API
export const configAPI = {
  getPublicConfig: () => axios.get(`${API_URL}/config/public`),
};
